import { ROUTES } from '@dealstack/frontend/investor/pages/Routing/routes';
import { DOCUMENT_STATUS } from '@dealstack/frontend/investor/pages/Valuations/Valuations';
import { getUserInvestmentsList } from '@dealstack/frontend/shared/dataExchange/services/shareholder/investment.service';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { DocumentStatusUIDetails } from './Valuations.types';

export enum VALUATIONS_DRAWER_TYPE {
	BANKING_DETAILS,
	PROGRAMME_CONTACTS,
	REQUIREMENTS_INSTRUCTIONS,
}

export const useValuationsController = () => {
	const { investorId } = useParams();
	const navigate = useNavigate();
	const tInvestor = useTranslationInvestor();

	const hasAllInvestmentsOption = investorId === undefined;
	const [currentDrawer, setCurrentDrawer] = useState<VALUATIONS_DRAWER_TYPE | undefined>();

	const getInvestmentListRequest = useQuery({
		queryKey: [API_PATHS.SHAREHOLDER_INVESTMENT_USER_INVESTMENTS_GET],
		queryFn: ({ signal }) => getUserInvestmentsList(signal),
	});

	const investment = useMemo(() => {
		if (hasAllInvestmentsOption) {
			return getInvestmentListRequest.data?.payload?.[0];
		}

		return getInvestmentListRequest.data?.payload?.find(
			(data) => data.investorId === investorId
		);
	}, [getInvestmentListRequest.data?.payload, hasAllInvestmentsOption, investorId]);

	const onCloseClicked = useCallback(() => {
		setCurrentDrawer(undefined);
	}, []);

	const documentStatusData: Record<DOCUMENT_STATUS, DocumentStatusUIDetails> = useMemo(() => {
		return {
			[DOCUMENT_STATUS.SIGNED]: {
				color: defaultTheme.v3.colors.green[8],
				text: tInvestor('valuations.documents_section.document_status.signed'),
			},
			[DOCUMENT_STATUS.COMPLETED]: {
				color: defaultTheme.v3.colors.dark[4],
				text: tInvestor('valuations.documents_section.document_status.completed'),
			},
			[DOCUMENT_STATUS.EXPIRED]: {
				color: defaultTheme.v3.colors.red[4],
				text: tInvestor('valuations.documents_section.document_status.expired'),
			},
			[DOCUMENT_STATUS.REJECTED]: {
				color: defaultTheme.v3.colors.red[4],
				text: tInvestor('valuations.documents_section.document_status.rejected'),
			},
		};
	}, []);

	const programmeContactPersons = useMemo(
		() =>
			investment
				? investment.contactPeople.map((person) => ({
						mail: person.email,
						firstName: person.firstName,
						lastName: person.lastName,
					}))
				: [],
		[investment]
	);

	const redirectToTransactionLists = () => {
		let pathName = ROUTES.TRANSACTIONS.toString();
		if (investorId) {
			pathName = generatePath(ROUTES.TRANSACTIONS_BY_INVESTOR_ID, { investorId });
		}
		navigate(pathName);
	};
	return {
		companyName: investment?.entityName,
		currentDrawer,
		setCurrentDrawer,
		programmeContactPersons: programmeContactPersons ?? [],
		onCloseClicked,
		hasAllInvestmentsOption,
		documentStatusData,
		redirectToTransactionLists,
	};
};
